import { getDbSupabase } from './supabase';
import { formatDeliveryDateDisplay, parseDeliveryDateForDb } from './demDeliveryDate';
import { formatSupabaseError } from './formatSupabaseError';
import { collectCustomerCodesFromRows, fetchCustomerNamesByCodes } from './demCustomerNames';
import {
  assertDemActorReady,
  fetchDemUserDisplayNameMap,
  resolveDemDisplayUser,
} from './demUserNames';
import { formatCheckedAtDisplay } from './demOrderChecked';
import type {
  DemCallInOrder,
  DemNotifyOrder,
  DemPoOrderFilter,
  DemPoOrderItem,
} from '../types/dem';
import {
  sendPendingOrderNotifications,
  sendProcessedOrderNotifications,
  type PendingNotificationResult,
  type ProcessedNotificationResult,
} from './demOrderNotifications';
import {
  formatOrderCreatorDisplay,
  formatOrderExporterDisplay,
  parseOrderBatches,
} from './demOrderBatches';
import { buildBatchAwareExportRows, finalizeOrderExport } from './demOrderExport';

const SGT = 'Asia/Singapore';
const ORDER_SELECT =
  'order_id, created_at, delivery_date, customer_code, ref_no, remarks, status, created_by, exported_by, checked_at, checked_by, items:order_items(item_code, description, qty, uom, batch_id), batches:order_batches(id, order_id, batch_no, reason, created_by, created_at, exported_by, exported_at)';

const INITIAL_PAGE = 50;
const MORE_PAGE = 100;

function parseCreatedAtDisplay(raw: string | null | undefined): string {
  if (!raw) return '—';
  try {
    const normalized = raw.includes('T') ? raw.replace('Z', '+00:00') : `${raw.replace(' ', 'T')}+00:00`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return raw;
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: SGT,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);
  } catch {
    return raw;
  }
}

function parseItems(raw: unknown): DemPoOrderItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => {
    const r = row as Record<string, unknown>;
    return {
      item_code: String(r.item_code ?? '').trim(),
      description: String(r.description ?? '').trim(),
      qty: Number(r.qty ?? 0),
      uom: String(r.uom ?? '').trim(),
      batch_id: r.batch_id == null ? null : String(r.batch_id).trim() || null,
    };
  });
}

function mapOrderRow(
  row: Record<string, unknown>,
  customerNames: Map<string, string>,
  userNames: Map<string, string>,
): DemCallInOrder {
  const customerCode = String(row.customer_code ?? '').trim();
  const customerName =
    (customerCode ? customerNames.get(customerCode) : null) ?? 'Unknown';
  const createdByFallback = String(row.created_by ?? '').trim();
  const batchesRaw = parseOrderBatches(row.batches);
  const batches = batchesRaw.map((batch) => ({
    ...batch,
    created_by: resolveDemDisplayUser(
      batch.created_by.trim() || (batch.batch_no === 0 ? createdByFallback : ''),
      userNames,
    ),
    exported_by: batch.exported_by
      ? resolveDemDisplayUser(batch.exported_by, userNames)
      : null,
  }));

  return {
    order_id: String(row.order_id ?? ''),
    customer_code: customerCode,
    customer_name: customerName,
    created_at: String(row.created_at ?? ''),
    created_at_display: parseCreatedAtDisplay(String(row.created_at ?? '')),
    delivery_date: String(row.delivery_date ?? ''),
    delivery_date_display: formatDeliveryDateDisplay(String(row.delivery_date ?? '')),
    ref_no: String(row.ref_no ?? '').trim() || '—',
    remarks: String(row.remarks ?? '').trim(),
    status: String(row.status ?? ''),
    created_by: formatOrderCreatorDisplay(batchesRaw, userNames, createdByFallback),
    exported_by: formatOrderExporterDisplay(batchesRaw, userNames, ''),
    checked_at: row.checked_at ? String(row.checked_at) : null,
    checked_at_display: formatCheckedAtDisplay(row.checked_at ? String(row.checked_at) : null),
    checked_by: resolveDemDisplayUser(String(row.checked_by ?? ''), userNames),
    items: parseItems(row.items),
    batches,
  };
}

async function mapOrderRows(rows: Record<string, unknown>[]): Promise<DemCallInOrder[]> {
  const [customerNames, userNames] = await Promise.all([
    fetchCustomerNamesByCodes(collectCustomerCodesFromRows(rows)),
    fetchDemUserDisplayNameMap(),
  ]);
  return rows.map((row) => mapOrderRow(row, customerNames, userNames));
}

function singaporeTodayDisplay(): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: SGT,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date());
}

export function filterCallInOrdersClient(
  orders: DemCallInOrder[],
  search: string,
  statusFilter: DemPoOrderFilter,
): DemCallInOrder[] {
  const keywords = search
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  const today = singaporeTodayDisplay();

  return orders.filter((order) => {
    if (statusFilter === 'Pending' && order.status !== 'Pending') return false;
    if (
      statusFilter === 'TodayProcessed' &&
      (order.status !== 'Processed' || !order.created_at_display.includes(today))
    ) {
      return false;
    }
    if (statusFilter === 'NeedsCheck' && (order.status !== 'Processed' || order.checked_at)) {
      return false;
    }

    if (keywords.length === 0) return true;

    const pool = [
      order.order_id,
      order.customer_code,
      order.customer_name,
      order.ref_no,
      order.remarks,
      order.status,
      order.created_by,
      order.exported_by,
      order.checked_by,
      ...order.items.map((item) => `${item.item_code} ${item.description}`),
    ]
      .join(' ')
      .toLowerCase();

    return keywords.every((kw) => pool.includes(kw));
  });
}

export async function fetchCallInOrdersPage(
  offset: number,
  limit: number,
  searchKw?: string,
): Promise<DemCallInOrder[]> {
  const db = getDbSupabase('dem');

  let query = db.from('orders').select(ORDER_SELECT).eq('source', 'CallIn');

  const kw = searchKw?.trim();
  if (kw) {
    query = query.or(
      `order_id.ilike.%${kw}%,customer_code.ilike.%${kw}%,ref_no.ilike.%${kw}%,remarks.ilike.%${kw}%`,
    );
  } else {
    query = query.neq('status', 'Archived');
  }

  const { data, error } = await query
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw new Error(formatSupabaseError(error));

  return mapOrderRows((data ?? []) as Record<string, unknown>[]);
}

export function mergeCallInOrders(
  existing: DemCallInOrder[],
  incoming: DemCallInOrder[],
): DemCallInOrder[] {
  const map = new Map(existing.map((o) => [o.order_id, o]));
  for (const row of incoming) map.set(row.order_id, row);
  return [...map.values()].sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export function callInOrderToNotifyOrder(order: DemCallInOrder): DemNotifyOrder {
  return {
    order_id: order.order_id,
    customer_code: order.customer_code,
    customer_name: order.customer_name,
    ref_no: order.ref_no,
    delivery_date_display: order.delivery_date_display,
    email: '',
    items: order.items,
  };
}

async function fetchPendingCallInOrders(createdByIds?: string[]): Promise<DemCallInOrder[]> {
  const db = getDbSupabase('dem');

  let query = db
    .from('orders')
    .select(ORDER_SELECT)
    .eq('status', 'Pending')
    .eq('source', 'CallIn');

  const owners = (createdByIds ?? []).map((id) => id.trim()).filter(Boolean);
  // Empty owners must not mean “everyone’s pending”.
  if (owners.length === 0) return [];
  if (owners.length === 1) {
    query = query.eq('created_by', owners[0]!);
  } else {
    query = query.in('created_by', owners);
  }

  const { data, error } = await query;
  if (error) throw new Error(formatSupabaseError(error));

  return mapOrderRows((data ?? []) as Record<string, unknown>[]);
}

export type CloudExportResult = {
  exported: number;
  notifications: ProcessedNotificationResult;
};

export async function exportPendingCallInOrdersToCloudQueue(
  exportedBy: string,
  createdByIds?: string[],
): Promise<CloudExportResult> {
  assertDemActorReady(exportedBy);
  const orders = await fetchPendingCallInOrders(createdByIds ?? [exportedBy]);
  if (orders.length === 0) {
    return { exported: 0, notifications: { sent: 0, skippedNoEmail: 0, failed: [] } };
  }

  const { hdrRows, detRows, orderIds, batchIds } = buildBatchAwareExportRows(orders);
  if (orderIds.length === 0 || (hdrRows.length === 0 && detRows.length === 0)) {
    return { exported: 0, notifications: { sent: 0, skippedNoEmail: 0, failed: [] } };
  }

  const batchId = `CI_${new Date().toISOString().replace(/[-:TZ.]/g, '').slice(0, 14)}_${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
  const db = getDbSupabase('dem');

  const { error: queueError } = await db.from('export_queue').insert({
    batch_id: batchId,
    target_folder: 'sales_order',
    order_ids: orderIds,
    exported_by: exportedBy,
    hdr_rows: hdrRows,
    det_rows: detRows,
    status: 'pending',
  });
  if (queueError) throw new Error(formatSupabaseError(queueError));

  await finalizeOrderExport({ orderIds, batchIds, exportedBy });

  const notifications = await sendProcessedOrderNotifications(orders.map(callInOrderToNotifyOrder));

  return { exported: orderIds.length, notifications };
}

export async function voidCallInOrder(orderId: string, voidedBy: string): Promise<void> {
  const db = getDbSupabase('dem');
  const { error } = await db
    .from('orders')
    .update({
      status: 'Cancelled',
      exported_by: `${voidedBy} (Void)`,
    })
    .eq('order_id', orderId)
    .eq('source', 'CallIn');

  if (error) throw new Error(formatSupabaseError(error));
}

export {
  markOrderChecked as markCallInOrderChecked,
  unmarkOrderChecked as unmarkCallInOrderChecked,
} from './demOrderChecked';

export function callInOrderStatusTone(
  status: string,
): 'pending' | 'uploading' | 'processed' | 'cancelled' | 'default' {
  const s = status.toLowerCase();
  if (s === 'pending') return 'pending';
  if (s === 'uploading') return 'uploading';
  if (s === 'processed') return 'processed';
  if (s === 'cancelled') return 'cancelled';
  return 'default';
}

export function callInOrderStatusIcon(status: string): string {
  const s = status.toLowerCase();
  if (s === 'processed') return '✓';
  if (s === 'uploading') return '⏳';
  if (s === 'cancelled') return '✕';
  if (s === 'archived') return '🗄';
  return '📞';
}

export const CALL_IN_ORDERS_INITIAL_PAGE = INITIAL_PAGE;
export const CALL_IN_ORDERS_MORE_PAGE = MORE_PAGE;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function getNextCallInNumber(): Promise<string> {
  const db = getDbSupabase('dem');
  const maxRetries = 3;

  for (let attempt = 0; attempt < maxRetries; attempt += 1) {
    const { data, error } = await db.rpc('generate_call_in_number');
    if (error) throw new Error(formatSupabaseError(error));
    const id = String(data ?? '').trim();
    if (id && id.toLowerCase() !== 'none') return id;
    if (attempt === maxRetries - 1) {
      throw new Error('Failed to generate Manual order number from server.');
    }
  }

  throw new Error('Failed to generate Manual order number from server.');
}

export type CallInLineInput = {
  item_code: string;
  description: string;
  qty: number;
  uom: string;
};

export type SaveCallInOrderInput = {
  customer_code: string;
  delivery_date: string;
  delivery_instruction: string;
  lines: CallInLineInput[];
  created_by: string;
  ref_no?: string;
  processing_seconds?: number | null;
};

export type SaveCallInOrderResult = {
  order_id: string;
  notifications: PendingNotificationResult;
};

export async function saveCallInOrderToPending(
  input: SaveCallInOrderInput,
): Promise<SaveCallInOrderResult> {
  assertDemActorReady(input.created_by);
  const customerCode = input.customer_code.trim();
  if (!customerCode) {
    throw new Error('Customer number is required.');
  }

  const lines = input.lines
    .map((line) => ({
      item_code: line.item_code.trim().toUpperCase(),
      description: line.description.trim(),
      qty: Number(line.qty),
      uom: line.uom.trim() || 'PCS',
    }))
    .filter((line) => line.item_code && Number.isFinite(line.qty) && line.qty > 0);

  if (lines.length === 0) {
    throw new Error('Add at least one item with quantity.');
  }

  const refNo = input.ref_no?.trim() || '';
  if (refNo) {
    const { assertOrderWithinBlanketOutstanding } = await import('./demBlanketOrders');
    await assertOrderWithinBlanketOutstanding({
      poNumber: refNo,
      lines,
    });
  }

  const db = getDbSupabase('dem');
  let orderId = await getNextCallInNumber();
  const dbDate = parseDeliveryDateForDb(input.delivery_date);
  const remarks = input.delivery_instruction.trim();

  const orderPayload: Record<string, unknown> = {
    order_id: orderId,
    ref_no: refNo,
    customer_code: customerCode,
    remarks,
    status: 'Pending',
    source: 'CallIn',
    created_by: input.created_by,
  };
  if (dbDate) orderPayload.delivery_date = dbDate;
  if (input.processing_seconds != null && input.processing_seconds > 0) {
    orderPayload.processing_seconds = Math.floor(input.processing_seconds);
  }

  const maxRetries = 3;
  let inserted = false;

  for (let attempt = 0; attempt < maxRetries; attempt += 1) {
    const { error } = await db.from('orders').insert(orderPayload);
    if (!error) {
      inserted = true;
      break;
    }

    const errStr = formatSupabaseError(error).toLowerCase();
    if (errStr.includes('23505') || errStr.includes('duplicate')) {
      if (attempt === maxRetries - 1) {
        throw new Error(`Duplicate Manual order clash for ${orderId}. Please try again.`);
      }
      orderId = await getNextCallInNumber();
      orderPayload.order_id = orderId;
      if (!input.ref_no?.trim()) orderPayload.ref_no = '';
      continue;
    }

    if (attempt === maxRetries - 1) throw new Error(formatSupabaseError(error));
    await sleep(1500);
  }

  if (!inserted) {
    throw new Error('Failed to insert Manual order header.');
  }

  const itemsPayload = lines.map((item) => ({
    order_id: orderId,
    item_code: item.item_code,
    description: item.description,
    qty: item.qty,
    uom: item.uom,
  }));

  const { error: itemsError } = await db.from('order_items').insert(itemsPayload);
  if (itemsError) {
    await db.from('orders').delete().eq('order_id', orderId);
    throw new Error(formatSupabaseError(itemsError));
  }

  const customerNames = await fetchCustomerNamesByCodes([customerCode]);
  const notifyOrder: DemNotifyOrder = {
    order_id: orderId,
    customer_code: customerCode,
    customer_name: customerNames.get(customerCode) ?? customerCode,
    ref_no: String(orderPayload.ref_no ?? '').trim() || '—',
    delivery_date_display: formatDeliveryDateDisplay(dbDate ?? input.delivery_date),
    email: '',
    items: lines,
  };

  const notifications = await sendPendingOrderNotifications([notifyOrder]);

  return { order_id: orderId, notifications };
}
